/** Map Development Express `transactions` rows → advance ledger PDF format */

import { getBookingMachineCode } from '../bookingDisplay';

export const inferWalletTxnChannel = (tx = {}, ctx = {}) => {
  const direct = String(tx.channel || '').trim().toLowerCase();
  if (direct && direct !== 'general') return direct;

  const text = String(tx.description || '').toLowerCase();
  const ref = String(tx.reference || tx.ref || '').trim();
  const bookingByRef = ctx.bookingByRef || {};

  if (text.includes('[wallet_recharge]') || text.includes('admin_credit') || text.includes('admin_correction')
    || (tx.type === 'credit' && /^pay_/i.test(ref))) {
    return 'wallet_recharge';
  }
  if (text.includes('bank_transfer') || /^bank_utr:/i.test(ref)) {
    return 'wallet_recharge';
  }
  if (
    text.includes('[booking_advance]')
    || text.includes('[extension_advance]')
    || text.includes('[booking_balance]')
    || text.includes('booking advance')
    || /^BK/i.test(ref)
    || /^BAL_BK/i.test(ref)
    || /^EXT_ADV_/i.test(ref)
    || Boolean(bookingByRef[ref])
  ) {
    return 'booking_advance';
  }
  if (text.includes('[cancel_refund]') || text.includes('cancel_refund')) return 'refund';
  if (text.includes('[settlement_credit]') || text.includes('[settlement_debit]')) return 'settlement';
  if (text.includes('treasury_escrow') || text.includes('[booking_charge_escrow]')) return 'treasury_internal';
  if (text.includes('refund')) return 'refund';
  return direct || 'general';
};

export const inferWalletTxnStatus = (tx = {}) => {
  const direct = String(tx.status || '').trim().toLowerCase();
  if (direct && ['captured', 'failed', 'reversed'].includes(direct)) return direct;
  const text = String(tx.description || '').toLowerCase();
  if (text.includes('[captured]')) return 'captured';
  if (text.includes('[failed]')) return 'failed';
  if (text.includes('[reversed]') || text.includes('refund')) return 'reversed';
  return 'captured';
};

export const buildClientLookup = (users = []) => {
  const map = {};
  users.forEach((u) => {
    if (!u?.id) return;
    map[u.id] = {
      id: u.id,
      name: u.name || u.company || 'Client',
      company: u.company || '',
      role: u.role || 'client',
      report_tier: u.report_tier || 'standard',
      project_site: u.company || u.location || '—',
    };
  });
  return map;
};

export const buildBookingRefLookup = (bookings = []) => {
  const map = {};
  bookings.forEach((b) => {
    if (b?.booking_ref) map[String(b.booking_ref)] = b;
    if (b?.id) map[String(b.id)] = b;
  });
  return map;
};

const extractMachineCode = (description = '') => {
  const match = String(description).match(/\b([A-Z]{2,5}-\d{2,4})\b/i);
  return match ? match[1].toUpperCase() : '';
};

const isSystemTxn = (tx = {}) => {
  const blob = `${tx.reference || ''} ${tx.description || ''}`.toLowerCase();
  return /seed-reset|\[seed\]|diag\d|staff wallet reset|production prep/i.test(blob);
};

/** Treasury/settlement mirror rows — not client advance activity */
export const isInternalAdvanceLedgerTxn = (tx = {}) => {
  if (isSystemTxn(tx)) return true;
  const blob = `${tx.reference || ''} ${tx.description || ''}`.toLowerCase();
  return /settlement_credit|settlement_debit|treasury_escrow|cancel_treasury_debit|booking_charge_escrow/.test(blob);
};

export const WALLET_TXN_CATEGORY = {
  CLIENT: 'client',
  OWNER: 'owner',
  TREASURY: 'treasury',
  INTERNAL: 'internal',
};

export function walletTxnCategoryLabel(category) {
  const key = String(category || '').toLowerCase();
  if (key === WALLET_TXN_CATEGORY.CLIENT) return 'Client';
  if (key === WALLET_TXN_CATEGORY.OWNER) return 'Owner';
  if (key === WALLET_TXN_CATEGORY.TREASURY) return 'Treasury';
  return 'Internal';
}

/** Admin reconciliation: Client | Owner | Treasury | Internal */
export function inferWalletTxnCategory(tx = {}, clientLookup = {}, ctx = {}) {
  const blob = `${tx.reference || ''} ${tx.description || ''}`.toLowerCase();
  if (isSystemTxn(tx)) return WALLET_TXN_CATEGORY.INTERNAL;

  const channel = inferWalletTxnChannel(tx, ctx);
  if (
    channel === 'treasury_internal'
    || /treasury_escrow|booking_charge_escrow|cancel_treasury_debit/.test(blob)
  ) {
    return WALLET_TXN_CATEGORY.TREASURY;
  }

  const user = tx.user_id ? clientLookup[tx.user_id] : null;
  const partyName = `${user?.name || ''} ${user?.company || ''}`.toLowerCase();
  if (/treasury|escrow/.test(partyName)) return WALLET_TXN_CATEGORY.TREASURY;

  if (channel === 'settlement' || /settlement_credit|settlement_debit/.test(blob)) {
    return WALLET_TXN_CATEGORY.OWNER;
  }

  if (user?.role === 'owner') return WALLET_TXN_CATEGORY.OWNER;

  if (isClientAdvanceLedgerTxn(tx, clientLookup)) return WALLET_TXN_CATEGORY.CLIENT;

  if (user?.role === 'client') return WALLET_TXN_CATEGORY.CLIENT;

  return WALLET_TXN_CATEGORY.INTERNAL;
}

/** Client wallet recharge, booking debits, and cancel refunds only */
export const isClientAdvanceLedgerTxn = (tx = {}, clientLookup = {}) => {
  if (isInternalAdvanceLedgerTxn(tx)) return false;

  if (!tx.user_id) return false;

  const user = clientLookup[tx.user_id];
  if (!user || user.role !== 'client') return false;

  const name = `${user?.name || ''} ${user?.company || ''}`.toLowerCase();
  if (/treasury|escrow/.test(name)) return false;

  const channel = inferWalletTxnChannel(tx, {});
  return channel === 'wallet_recharge' || channel === 'booking_advance' || channel === 'refund';
};

const resolveClientName = (tx, clientLookup) => {
  if (isSystemTxn(tx)) return 'Staff / System';
  const client = clientLookup[tx.user_id] || {};
  return client.name || client.company || 'Unknown Client';
};

const resolveMachineCode = (tx, { machineLookup, bookingByRef }) => {
  const ref = String(tx.reference || '').trim();
  if (ref && bookingByRef[ref]) {
    const code = getBookingMachineCode(bookingByRef[ref], machineLookup);
    if (code && code !== 'N/A') return code;
  }

  const desc = String(tx.description || '');
  const uuidMatch = desc.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  );
  if (uuidMatch && machineLookup?.get) {
    const found = machineLookup.get(uuidMatch[0]);
    if (found?.code) return found.code;
  }

  return extractMachineCode(desc) || '—';
};

const resolveLedgerReference = (tx, { machineLookup, bookingByRef }) => {
  const desc = String(tx.description || '').trim();
  const ref = String(tx.reference || '').trim();
  const channel = inferWalletTxnChannel(tx, { bookingByRef });

  // Legacy rows already store a clean human-readable line in description.
  if (desc && /booking advance/i.test(desc) && !/\[captured\]|\[booking_advance\]/i.test(desc)) {
    return desc;
  }

  if (channel === 'booking_advance' && ref && bookingByRef[ref]) {
    const booking = bookingByRef[ref];
    const machineName =
      booking.machines?.name
      || machineLookup?.get(String(booking.machine_id))?.name
      || '';
    if (machineName) return `${machineName} Booking Advance`;

    const code = getBookingMachineCode(booking, machineLookup);
    if (code && code !== 'N/A') return `${code} Booking Advance`;
  }

  if (desc) return desc;
  return ref || '';
};

export const mapWalletTransactionsToLedger = (
  transactions = [],
  clientLookup = {},
  enrich = {},
) => {
  const machineLookup = enrich.machineLookup || null;
  const bookingByRef = enrich.bookingByRef || {};

  return (transactions || [])
    .filter((tx) => isClientAdvanceLedgerTxn(tx, clientLookup))
    .map((tx, idx) => {
    const channel = inferWalletTxnChannel(tx, { bookingByRef });
    const desc = String(tx.description || '');
    const amount = Math.abs(Number(tx.amount || 0));

    let type = 'RECEIPT';
    if (channel === 'refund' || desc.toLowerCase().includes('refund')) {
      type = 'REFUND';
    } else if (channel === 'booking_advance' || tx.type === 'debit') {
      type = 'ADJUSTED';
    } else if (tx.type === 'credit') {
      type = 'RECEIPT';
    }

    let payment_mode = 'Wallet';
    if (channel === 'wallet_recharge') payment_mode = 'UPI / Wallet';
    if (channel === 'booking_advance') payment_mode = 'Booking Advance';

    const txnDate = tx.created_at
      ? String(tx.created_at).slice(0, 10)
      : new Date().toISOString().slice(0, 10);

    return {
      txn_date: txnDate,
      receipt_no: tx.reference || `TXN-${String(tx.id || idx).slice(0, 8)}`,
      type,
      amount,
      payment_mode,
      reference: resolveLedgerReference(tx, { machineLookup, bookingByRef }),
      machine_code: resolveMachineCode(tx, { machineLookup, bookingByRef }),
      client_id: tx.user_id,
      client_name: resolveClientName(tx, clientLookup),
      project_site: clientLookup[tx.user_id]?.project_site || clientLookup[tx.user_id]?.company || '—',
      client_status: 'ACTIVE',
    };
  });
};

/** Premium chart data when ledger_advances RPC is unavailable */
export const buildMonthlyTrendFromLedger = (ledgerRows = [], months = 7) => {
  const now = new Date();
  const buckets = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleDateString('en-IN', { month: 'short' });
    const monthStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const prefix = monthStart.slice(0, 7);
    const value = ledgerRows
      .filter((r) => r.type === 'RECEIPT' && String(r.txn_date).startsWith(prefix))
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);
    buckets.push({ label, value, month_start: monthStart });
  }
  return buckets;
};
